import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Settings2,
  Building2,
  CreditCard,
  Users,
  Shield,
  Smartphone,
  Palette,
  Globe,
  MessageSquare,
  Database,
  Link2,
  Lock,
  FileText,
  FlaskConical,
  Save,
  Loader2,
  Eye,
  EyeOff,
  Pencil,
  Ban,
  Info,
  RefreshCw,
  CheckCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Settings sections configuration with default editability
const SETTINGS_SECTIONS = [
  {
    group: 'General',
    items: [
      { key: 'agency_profile', label: 'Agency Profile', icon: Building2, default: 'editable' },
      { key: 'billing_membership', label: 'Billing & Membership', icon: CreditCard, default: 'read_only' },
      { key: 'my_staff', label: 'My Staff', icon: Users, default: 'editable' },
    ],
  },
  {
    group: 'Portal & Branding',
    items: [
      { key: 'portal_access', label: 'Portal Access', icon: Shield, default: 'read_only' },
      { key: 'portals_mobile_app', label: 'Portals & Mobile App', icon: Smartphone, default: 'editable' },
      { key: 'branding_white_label', label: 'Branding & White Label', icon: Palette, default: 'locked', requiresEntitlement: 'white_label' },
      { key: 'domains_redirects', label: 'Domains & Redirects', icon: Globe, default: 'editable', requiresEntitlement: 'custom_domain' },
    ],
  },
  {
    group: 'Communication & Services',
    items: [
      { key: 'communication_settings', label: 'Communication Settings', icon: MessageSquare, default: 'editable', requiresEntitlement: 'communication' },
    ],
  },
  {
    group: 'Data & System',
    items: [
      { key: 'custom_fields', label: 'Custom Fields', icon: Database, default: 'editable' },
      { key: 'custom_values', label: 'Custom Values', icon: Database, default: 'editable' },
      { key: 'integrations', label: 'Integrations', icon: Link2, default: 'read_only' },
      { key: 'private_integrations', label: 'Private Integrations', icon: Lock, default: 'hidden', requiresEntitlement: 'api_access' },
    ],
  },
  {
    group: 'Governance',
    items: [
      { key: 'audit_logs', label: 'Audit Logs', icon: FileText, default: 'read_only' },
      { key: 'advanced_labs', label: 'Advanced / Labs', icon: FlaskConical, default: 'hidden' },
    ],
  },
];

// Access modes with their icons and descriptions
const ACCESS_MODES = [
  { value: 'editable', label: 'Editable', icon: Pencil, color: 'text-emerald-600', bg: 'bg-emerald-100', description: 'Agency owner can view and edit' },
  { value: 'read_only', label: 'Read Only', icon: Eye, color: 'text-blue-600', bg: 'bg-blue-100', description: 'Agency owner can view but not edit' },
  { value: 'locked', label: 'Locked', icon: Lock, color: 'text-amber-600', bg: 'bg-amber-100', description: 'Visible with upgrade prompt' },
  { value: 'hidden', label: 'Hidden', icon: EyeOff, color: 'text-slate-500', bg: 'bg-slate-100', description: 'Not visible to agency owner' },
];

/**
 * Settings Control Tab - Platform Admin Surface
 * 
 * This is the MASTER CONTROL SURFACE that governs what agency owners
 * can see and edit in their Agency Settings workspace.
 * 
 * Lives at: Platform Console → Agencies → Agency Detail → Settings Control
 */
const AgencySettingsControlTab = ({ agencyId, agencyName }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsControl, setSettingsControl] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  
  const headers = { Authorization: `Bearer ${token}` };
  
  // Initialize with defaults
  useEffect(() => {
    const initializeDefaults = () => {
      const defaults = {};
      SETTINGS_SECTIONS.forEach(group => {
        group.items.forEach(item => {
          defaults[item.key] = item.default;
        });
      });
      return defaults;
    };
    
    const fetchSettingsControl = async () => {
      try {
        const response = await axios.get(
          `${API}/admin/agencies/${agencyId}/settings-control`,
          { headers }
        );
        
        // Merge fetched settings with defaults
        const defaults = initializeDefaults();
        setSettingsControl({
          ...defaults,
          ...(response.data?.settings_control || {}),
        });
      } catch (err) {
        // Use defaults if fetch fails
        setSettingsControl(initializeDefaults());
      } finally {
        setLoading(false);
      }
    };
    
    if (token && agencyId) {
      fetchSettingsControl();
    }
  }, [token, agencyId]);
  
  // Handle access mode change
  const handleAccessChange = (sectionKey, value) => {
    setSettingsControl(prev => ({
      ...prev,
      [sectionKey]: value,
    }));
    setHasChanges(true);
  };
  
  // Reset to defaults
  const handleResetDefaults = () => {
    const defaults = {};
    SETTINGS_SECTIONS.forEach(group => {
      group.items.forEach(item => {
        defaults[item.key] = item.default;
      });
    });
    setSettingsControl(defaults);
    setHasChanges(true);
  };
  
  // Save settings
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/agencies/${agencyId}/settings-control`,
        { settings_control: settingsControl },
        { headers }
      );
      
      toast.success('Settings control saved successfully');
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to save settings control:', err);
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  // Get access mode details
  const getAccessMode = (value) => ACCESS_MODES.find(m => m.value === value) || ACCESS_MODES[3];
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="settings-control-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
            <Settings2 className="text-white" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Settings Control</h2>
            <p className="text-sm text-slate-500">
              Control what {agencyName || 'this agency'} can access in their settings
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetDefaults}
          >
            <RefreshCw size={14} className="mr-2" />
            Reset to Defaults
          </Button>
          
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
      
      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <Info className="text-amber-600 mt-0.5" size={18} />
        <div className="text-sm text-amber-800">
          <p className="font-medium">Platform Admin Control Surface</p>
          <p className="text-amber-700 mt-1">
            This page governs what agency owners can see and modify in their Settings workspace.
            Changes here apply immediately to the agency owner's view.
          </p>
        </div>
      </div>
      
      {/* Access Mode Legend */}
      <div className="flex items-center gap-4 flex-wrap">
        {ACCESS_MODES.map(mode => {
          const Icon = mode.icon;
          return (
            <TooltipProvider key={mode.value}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 cursor-help">
                    <Badge className={`${mode.bg} ${mode.color} border-0`}>
                      <Icon size={12} className="mr-1" />
                      {mode.label}
                    </Badge>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{mode.description}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
      
      {/* Settings Control Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {SETTINGS_SECTIONS.map((section, sectionIdx) => (
          <div key={section.group}>
            {/* Group Header */}
            <div className="px-6 py-3 bg-slate-50 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                {section.group}
              </h3>
            </div>
            
            {/* Group Items */}
            <table className="w-full">
              <tbody>
                {section.items.map((item, itemIdx) => {
                  const Icon = item.icon;
                  const currentMode = getAccessMode(settingsControl[item.key]);
                  const ModeIcon = currentMode.icon;
                  
                  return (
                    <tr
                      key={item.key}
                      className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/50 ${
                        itemIdx === section.items.length - 1 && sectionIdx < SETTINGS_SECTIONS.length - 1
                          ? 'border-b-2'
                          : ''
                      }`}
                    >
                      <td className="px-6 py-4 w-1/3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                            <Icon size={18} className="text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{item.label}</p>
                            {item.requiresEntitlement && (
                              <p className="text-xs text-amber-600">
                                Requires: {item.requiresEntitlement}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 w-1/3">
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <span>Default:</span>
                          <Badge variant="outline" className="text-xs">
                            {item.default}
                          </Badge>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 w-1/3">
                        <Select
                          value={settingsControl[item.key]}
                          onValueChange={(value) => handleAccessChange(item.key, value)}
                        >
                          <SelectTrigger className={`w-44 bg-white ${currentMode.color}`}>
                            <div className="flex items-center gap-2">
                              <ModeIcon size={14} />
                              <SelectValue />
                            </div>
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            {ACCESS_MODES.map(mode => {
                              const MIcon = mode.icon;
                              return (
                                <SelectItem key={mode.value} value={mode.value}>
                                  <div className="flex items-center gap-2">
                                    <MIcon size={14} className={mode.color} />
                                    <span>{mode.label}</span>
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
      
      {/* Changes Indicator */}
      {hasChanges && (
        <div className="fixed bottom-6 right-6 bg-amber-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
          <CheckCircle size={16} />
          <span className="text-sm font-medium">Unsaved changes</span>
        </div>
      )}
    </div>
  );
};

export default AgencySettingsControlTab;
